import { ChartConfig, ChartData, ChartDataset } from '@components/svg-chart';
import { InstanceFillStepSnapshot } from '@6grain/masofa-monolith-api-client/common/model/instanceFillStepSnapshot';

export type InstanceFillStatus = 'Filled' | 'Empty' | 'Unavailable';

export interface InstanceFillNavigateTarget {
  commands: string[];
  queryParams?: Record<string, string>;
}

export interface InstanceFillTotalsVm {
  recordCount: number;
  activeCount: number;
  hasActiveCount: boolean;
  filledSteps: number;
  emptySteps: number;
  unavailableSteps: number;
}

export interface InstanceFillCardVm {
  id: string;
  titleKey: string;
  status: InstanceFillStatus;
  statusColor: 'success' | 'danger' | 'info';
  count: number | null;
  activeCount: number | null;
  error: string | null;
  fillTarget: InstanceFillNavigateTarget | null;
  searchHaystack: string[];
}

export const INSTANCE_FILL_STEP_TITLE_KEYS: Record<string, string> = {
  'weather-process-subscriptions': 'Weather subscriptions',
  'satellite-process-subscriptions': 'Satellite subscriptions',
  fields: 'Fields',
  regions: 'Regions',
};

const INSTANCE_FILL_SEARCH_ALIASES: Record<string, readonly string[]> = {
  'weather-process-subscriptions': ['Weather subscriptions', 'Подписки на погоду', 'Ob-havo obunalari'],
  'satellite-process-subscriptions': ['Satellite subscriptions', 'Подписки на спутники', 'Sun\'iy yo\'ldosh obunalari'],
  fields: ['Fields', 'Поля', 'Maydonlar'],
  regions: ['Regions', 'Регионы', 'Mintaqalar'],
};

export function instanceFillTitleKey(id: string): string {
  return INSTANCE_FILL_STEP_TITLE_KEYS[id] ?? id;
}

export function instanceFillNavigateTarget(id: string): InstanceFillNavigateTarget | null {
  if (id === 'weather-process-subscriptions' || id === 'satellite-process-subscriptions') {
    return {
      commands: ['/admin/orchestration/subscriptions'],
      queryParams: { create: '1' },
    };
  }
  if (id === 'fields') {
    return { commands: ['/electronic-field-map/fields'] };
  }
  if (id === 'regions') {
    return { commands: ['/dictionaries/regions'] };
  }
  return null;
}

export function instanceFillStatus(status: string | null | undefined): InstanceFillStatus {
  if (status === 'Filled' || status === 'Empty' || status === 'Unavailable') {
    return status;
  }
  return 'Unavailable';
}

const FILL_STATUS_CHART_COLORS: Record<InstanceFillStatus, string> = {
  Filled: '#16a34a',
  Empty: '#d97706',
  Unavailable: '#64748b',
};

export function toInstanceFillTotals(
  steps: readonly InstanceFillStepSnapshot[],
): InstanceFillTotalsVm {
  let recordCount = 0;
  let activeCount = 0;
  let hasActiveCount = false;
  let filledSteps = 0;
  let emptySteps = 0;
  let unavailableSteps = 0;
  for (const step of steps) {
    const status = instanceFillStatus(step.status);
    if (status === 'Filled') {
      filledSteps += 1;
    } else if (status === 'Empty') {
      emptySteps += 1;
    } else {
      unavailableSteps += 1;
    }
    if (status !== 'Unavailable' && typeof step.count === 'number') {
      recordCount += step.count;
    }
    if (typeof step.activeCount === 'number') {
      hasActiveCount = true;
      activeCount += step.activeCount;
    }
  }
  return {
    recordCount,
    activeCount,
    hasActiveCount,
    filledSteps,
    emptySteps,
    unavailableSteps,
  };
}

export function buildInstanceFillCountChart(
  steps: readonly InstanceFillStepSnapshot[],
  translate: (key: string) => string,
): ChartData {
  const countable = steps.filter(step => {
    const status = instanceFillStatus(step.status);
    return status !== 'Unavailable' && typeof step.count === 'number';
  });
  if (countable.length === 0) {
    return { labels: [], datasets: [] };
  }
  const colors = countable.map(step => FILL_STATUS_CHART_COLORS[instanceFillStatus(step.status)]);
  return {
    labels: countable.map(step => translate(instanceFillTitleKey(step.id?.trim() ?? ''))),
    datasets: [fillChartDataset(translate('Count'), countable.map(step => step.count ?? 0), colors)],
  };
}

export function buildInstanceFillStatusChart(
  steps: readonly InstanceFillStepSnapshot[],
  translate: (key: string) => string,
): ChartData {
  if (steps.length === 0) {
    return { labels: [], datasets: [] };
  }
  const totals = toInstanceFillTotals(steps);
  const colors = [
    FILL_STATUS_CHART_COLORS.Filled,
    FILL_STATUS_CHART_COLORS.Empty,
    FILL_STATUS_CHART_COLORS.Unavailable,
  ];
  return {
    labels: [translate('Filled'), translate('Empty'), translate('Unavailable')],
    datasets: [fillChartDataset(
      translate('Status'),
      [totals.filledSteps, totals.emptySteps, totals.unavailableSteps],
      colors,
    )],
  };
}

function fillChartDataset(label: string, data: number[], colors: string[]): ChartDataset {
  return {
    label,
    data,
    borderColor: colors[0] ?? FILL_STATUS_CHART_COLORS.Unavailable,
    backgroundColor: colors as unknown as string,
  };
}

export function instanceFillCountChartConfig(): ChartConfig {
  return {
    indexAxis: 'y',
    plugins: { legend: { display: false } },
    scales: {
      x: { beginAtZero: true, ticks: { precision: 0 } },
    },
  } as ChartConfig;
}

export function instanceFillStatusChartConfig(): ChartConfig {
  return {
    plugins: { legend: { position: 'bottom' } },
  } as ChartConfig;
}

export function toInstanceFillCard(step: InstanceFillStepSnapshot): InstanceFillCardVm {
  const id = step.id?.trim() ?? '';
  const status = instanceFillStatus(step.status);
  const empty = status === 'Empty';
  return {
    id,
    titleKey: instanceFillTitleKey(id),
    status,
    statusColor: status === 'Filled' ? 'success' : empty ? 'danger' : 'info',
    count: step.count ?? null,
    activeCount: step.activeCount ?? null,
    error: step.error ?? null,
    fillTarget: empty && id ? instanceFillNavigateTarget(id) : null,
    searchHaystack: [id, instanceFillTitleKey(id), ...(INSTANCE_FILL_SEARCH_ALIASES[id] ?? [])],
  };
}
